import styled, { css, DefaultTheme, keyframes } from "styled-components";

import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

import { TIP_WIDGET_ANIMATION, TIP_WIDGET_TEXTS } from "@dineout-payment-success-ui/constants/TipWidget";

const ICONS_SLIDE_IN_OFFSET_PX = 18;
const ICONS_SLIDE_IN_START_OPACITY = 0.6;
const CLOUD_FADE_END_OPACITY = 0.9;
const CLOUD_EXPANDED_BOTTOM = "calc(-56px + var(--sab, 0px))";
const CLOUD_COLLAPSED_BOTTOM = "calc(-106px + var(--sab, 0px))";
const CLOUD_WIDTH_PX = 481;
const CLOUD_HEIGHT_PX = 320;
const HEADER_COLLAPSED_PADDING_RIGHT = 72;
const TILE_SHIFT_PX = 79;
const TILE_COLLAPSE_DELAY_MS = 30;
const STICKY_BOTTOM_NUDGE_PX = 2;
const TIP_CTA_HIDDEN_OFFSET = -80;
const TIP_CTA_WIDTH = 60;
const PAYMENT_OPTION_TILE_HEIGHT = "44px";
const PAYMENT_OPTION_DIVIDER_WIDTH = "1px";
const MORPH_TRANSITION = `${TIP_WIDGET_ANIMATION.MORPH_DURATION_MS}ms ${TIP_WIDGET_ANIMATION.MORPH_EASING}`;
const ENTRANCE_ANIMATION = `${TIP_WIDGET_ANIMATION.SLIDE_UP_DURATION_MS}ms ${TIP_WIDGET_ANIMATION.SLIDE_UP_EASING} ${TIP_WIDGET_TEXTS.LOAD_DELAY_MS}ms both`;

const getPaymentOptionsTransform = (
    isExpanded: boolean,
    isFirstLoad: boolean | undefined,
    theme: DefaultTheme,
): string => {
    if (!isExpanded) {
        return `translate3d(0, ${theme.spacing.small}, 0)`;
    }
    if (isFirstLoad) {
        return `translate3d(-${theme.spacing.x9}, 0, 0)`;
    }
    return "translate3d(0, 0, 0)";
};

const iconsSlideIn = keyframes`
    from {
        transform: translate3d(-${ICONS_SLIDE_IN_OFFSET_PX}px, 0, 0);
        opacity: ${ICONS_SLIDE_IN_START_OPACITY};
    }
    to {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
`;

const tipWidgetSlideUp = keyframes`
    from {
        transform: translate3d(0, 100%, 0);
        opacity: 0;
    }
    to {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
`;

const tipWidgetCloudFadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: ${CLOUD_FADE_END_OPACITY};
    }
`;

export const TipWidgetCloudBackground = styled.div<{ isExpanded: boolean }>`
    ${({ theme, isExpanded }) => ({
        position: "fixed",
        bottom: isExpanded ? CLOUD_EXPANDED_BOTTOM : CLOUD_COLLAPSED_BOTTOM,
        left: "50%",
        transform: "translateX(-50%)",
        width: `min(${CLOUD_WIDTH_PX}px, 100vw)`,
        aspectRatio: `${CLOUD_WIDTH_PX} / ${CLOUD_HEIGHT_PX}`,
        borderRadius: "50% / 30%",
        background: theme.colors[theme.colorMode].background_Primary,
        filter: `blur(${theme.spacing.x15})`,
        pointerEvents: "none",
        zIndex: theme.elevation[theme.colorMode].raised.zIndex,
        transition: `bottom ${MORPH_TRANSITION}`,
    })}
    animation: ${tipWidgetCloudFadeIn} ${ENTRANCE_ANIMATION};
`;

export const TipWidgetStickyContainer = styled.div<{ isInteractive: boolean; isExpanded: boolean }>`
    ${({ theme, isInteractive, isExpanded }) => ({
        position: "fixed",
        left: theme.spacing.medium,
        right: theme.spacing.medium,
        bottom: `calc(${theme.spacing.large} * 3 - ${STICKY_BOTTOM_NUDGE_PX}px + var(--sab, 0px))`,
        borderRadius: isExpanded ? theme.cornerRadius.medium : theme.cornerRadius.small,
        boxShadow: theme.elevation[theme.colorMode].floating.boxShadow,
        zIndex: theme.elevation[theme.colorMode].floating.zIndex,
        pointerEvents: isInteractive ? "auto" : "none",
        willChange: "transform, opacity",
        transition: `border-radius ${MORPH_TRANSITION}`,
    })}
    animation: ${tipWidgetSlideUp} ${ENTRANCE_ANIMATION};
`;

export const TipWidgetWrapper = styled.div<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: isExpanded ? theme.spacing.xSmall : theme.spacing.empty,
    width: "100%",
    paddingTop: isExpanded ? theme.spacing.x5 : theme.spacing.small,
    paddingBottom: theme.spacing.small,
    paddingLeft: isExpanded ? theme.spacing.small : theme.spacing.x7,
    paddingRight: isExpanded ? theme.spacing.small : theme.spacing.x7,
    background: theme.colors[theme.colorMode].background_Primary,
    border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    borderRadius: isExpanded ? theme.cornerRadius.medium : theme.cornerRadius.small,
    overflow: "hidden",
    transition: `border-radius ${MORPH_TRANSITION}, gap ${MORPH_TRANSITION}, padding ${MORPH_TRANSITION}`,
}));

export const TipWidgetHeader = styled.div<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
    display: "flex",
    alignItems: isExpanded ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: theme.spacing.xSmall,
    width: "100%",
    minHeight: isExpanded ? undefined : theme.spacing.xxLarge,
    paddingRight: isExpanded ? theme.spacing.xLarge : HEADER_COLLAPSED_PADDING_RIGHT,
    transition: `min-height ${MORPH_TRANSITION}, padding-right ${MORPH_TRANSITION}`,
}));

export const TipWidgetTextBlock = styled.div({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
});

export const SubtitleText = styled(Text)(() => ({
    display: "flex",
    alignItems: "center",
    transition: `font-size ${MORPH_TRANSITION}, line-height ${MORPH_TRANSITION}, letter-spacing ${MORPH_TRANSITION}`,
}));

export const ChevronButton = styled.button<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
    position: "absolute",
    top: theme.spacing.x5,
    right: theme.spacing.small,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: theme.spacing.medium,
    height: theme.spacing.medium,
    padding: 0,
    margin: 0,
    border: "none",
    background: "transparent",
    appearance: "none",
    WebkitTapHighlightColor: "transparent",
    cursor: "pointer",
    opacity: isExpanded ? 1 : 0,
    transform: isExpanded ? "scale(1)" : "scale(0.85)",
    pointerEvents: isExpanded ? "auto" : "none",
    transition: `opacity ${MORPH_TRANSITION}, transform ${MORPH_TRANSITION}`,
    "&:active": {
        transform: isExpanded ? "scale(0.96)" : "scale(0.85)",
    },
}));

export const PaymentOptionsRow = styled.div<{
    isExpanded: boolean;
    isFirstLoad?: boolean;
}>`
    ${({ theme, isExpanded, isFirstLoad }) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing.small,
        width: "100%",
        maxHeight: isExpanded ? PAYMENT_OPTION_TILE_HEIGHT : theme.spacing.empty,
        opacity: !isExpanded || isFirstLoad ? 0 : 1,
        transform: getPaymentOptionsTransform(isExpanded, isFirstLoad, theme),
        overflow: "visible",
        pointerEvents: isExpanded ? "auto" : "none",
        transition: `max-height ${MORPH_TRANSITION}, opacity ${MORPH_TRANSITION}, transform ${MORPH_TRANSITION}`,
    })}
    ${({ isFirstLoad }) =>
        isFirstLoad &&
        css`
            animation: ${iconsSlideIn} ${TIP_WIDGET_ANIMATION.SLIDE_UP_DURATION_MS}ms
                ${TIP_WIDGET_ANIMATION.SLIDE_UP_EASING} both;
        `}
`;

export const PaymentOptionTile = styled.button<{ isExpanded?: boolean; index?: number; isSingleOption?: boolean }>(
    ({ theme, isExpanded = true, index = 0, isSingleOption = false }) => ({
        display: "flex",
        flexDirection: isSingleOption ? "row" : "column",
        alignItems: "center",
        justifyContent: "center",
        gap: isSingleOption ? theme.spacing.x3 : undefined,
        flex: 1,
        height: PAYMENT_OPTION_TILE_HEIGHT,
        padding: 0,
        background: theme.colors[theme.colorMode].background_Primary,
        border: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
        borderRadius: theme.cornerRadius.small,
        boxShadow: theme.elevation[theme.colorMode].lifted.boxShadow,
        cursor: "pointer",
        overflow: "hidden",
        transition: `transform ${MORPH_TRANSITION}`,
        transform: isExpanded ? "translate3d(0, 0, 0)" : `translate3d(-${TILE_SHIFT_PX}px, ${theme.spacing.xSmall}, 0)`,
        transitionDelay: isExpanded ? "0ms" : `${index * TILE_COLLAPSE_DELAY_MS}ms`,
        "&:active": {
            transform: "scale(0.96)",
        },
    }),
);

export const PaymentOptionLabel = styled(Text)(({ theme }) => ({
    marginTop: `calc(-1 * ${theme.spacing.xxSmall})`,
    marginBottom: theme.spacing.xxSmall,
}));

export const TipCtaWrap = styled.div<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
    position: "absolute",
    bottom: theme.spacing.small,
    right: isExpanded ? TIP_CTA_HIDDEN_OFFSET : theme.spacing.small,
    opacity: isExpanded ? 0 : 1,
    pointerEvents: isExpanded ? "none" : "auto",
    transition: `right ${MORPH_TRANSITION}, opacity ${MORPH_TRANSITION}`,
}));

export const TipCtaButton = styled(SuperButton)(({ theme }) => ({
    width: TIP_CTA_WIDTH,
    marginBottom: theme.spacing.xxxSmall,
}));
export const UpiTextWrapper = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xxxSmall,
    borderLeft: `${PAYMENT_OPTION_DIVIDER_WIDTH} solid ${theme.colors[theme.colorMode].divider}`,
    paddingLeft: theme.spacing.xSmall,
}));

export const SingleOptionChevron = styled.div(({ theme }) => ({
    display: "flex",
    flexShrink: 0,
    width: theme.spacing.xSmall,
    height: theme.spacing.xSmall,
    lineHeight: 0,
    svg: {
        width: "100%",
        height: "100%",
        display: "block",
    },
}));
